import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import appConfig from './config/appConfig';

// Dialog styles matching BusinessTeams component
const DialogStyles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000
  },
  dialog: {
    backgroundColor: 'white',
    padding: '20px',
    borderRadius: '8px',
    width: '400px',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  },
  title: {
    fontSize: '18px',
    textAlign: 'center',
    margin: 0,
    color: '#000',
    backgroundColor: 'white',
  },
  button: {
    backgroundColor: '#FFFDD0',
    border: 'none',
    borderRadius: '4px',
    padding: '10px',
    width: '100%',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: 500,
    color: 'black'
  }
};

const ThankYouDialog = ({ isOpen, onClose, submissionType }) => {
  if (!isOpen) return null;

  const getSubmissionTypeText = () => {
    switch(submissionType) {
      case 'demo': return 'demo request';
      case 'feedback': return 'feedback';
      case 'question': return 'question';
      default: return 'submission';
    }
  };

  return (
    <div style={DialogStyles.overlay}>
      <div style={{...DialogStyles.dialog, borderRadius: '16px', border: '1px solid #ccc'}}>
        <div style={{
          backgroundColor: '#E8F5E8',
          color: '#2D5A2D',
          padding: '15px 0',
          textAlign: 'center',
          fontSize: '18px',
          fontWeight: 'bold',
          borderRadius: '8px 8px 0 0',
          margin: '-20px -20px 20px -20px'
        }}>
          <div className="flex items-center justify-center gap-2">
            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span>Thank You!</span>
          </div>
        </div>
        <h2 style={{
          ...DialogStyles.title,
          fontSize: '16.8px',
          lineHeight: '1.4',
        }}>
          Your request has been submitted successfully.
          <br />
          We'll get back to you soon.
          <br />
          <br />
          Thank you for your interest in ReqSharp!
        </h2>
        <button
          style={{
            ...DialogStyles.button,
            backgroundColor: '#FFFDD0',
            width: '120px',
            margin: '0 auto',
            display: 'block'
          }}
          onClick={onClose}
        >
          OK
        </button>
      </div>
    </div>
  );
};

const SubmitFeedback = () => {
  const [formData, setFormData] = useState({
    type: 'feedback', // 'feedback', 'question', or 'demo'
    subject: '',
    description: '',
    priority: 'medium',
    category: 'general',
    attachments: []
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [showThankYouDialog, setShowThankYouDialog] = useState(false);
  const [userInfo, setUserInfo] = useState({
    name: '',
    email: '',
    project: '',
    company: ''
  });
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  const navigate = useNavigate();

  // Get user information from localStorage and API
  useEffect(() => {
    const fetchUserInfo = async () => {
      const user_id = localStorage.getItem('user_id');
      const current_project = localStorage.getItem('current_project');
      const login_token = localStorage.getItem('authToken');

      if (user_id && current_project && login_token) {
        setIsLoggedIn(true);
        try {
          const response = await fetch(appConfig.getUrl('/get_user_info'), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${login_token}`
            },
            body: JSON.stringify({
              user_id,
              project_id: current_project
            })
          });

          const userData = await response.json();
          console.log('User Data:', userData);

          if (userData.status === 'Success') {
            setUserInfo({
              name: userData.user_name || '',
              email: userData.user_id|| '',
              project: current_project || userData.default_project || '',
              company: [userData.corporate_account, userData.account_description].filter(Boolean).join(' - ') || ''
            });
          }
        } catch (error) {
          console.error('Error fetching user info:', error);
        }
      } else {
        setIsLoggedIn(false);
        // Initialize empty user info for non-logged-in users
        setUserInfo({
          name: '',
          email: '',
          project: '',
          company: ''
        });
      }
    };

    fetchUserInfo();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const newFormData = {
        ...prev,
        [name]: value
      };

      // Handle type change logic
      if (name === 'type') {
        if (value === 'demo') {
          newFormData.category = 'demo';
          newFormData.priority = 'high';
          newFormData.subject = 'Requesting Demo';
        } else if (prev.type === 'demo') {
          // Reset defaults when moving away from demo
          newFormData.category = 'general';
          newFormData.priority = 'medium';
          if (newFormData.subject === 'Requesting Demo') {
            newFormData.subject = '';
          }
        }
      }

      return newFormData;
    });
  };

  const handleUserInfoChange = (e) => {
    const { name, value } = e.target;
    setUserInfo(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    const maxSize = 10 * 1024 * 1024; // 10MB
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf', 'text/plain', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];

    const validFiles = files.filter(file => {
      if (file.size > maxSize) {
        alert(`File ${file.name} is too large. Maximum size is 10MB.`);
        return false;
      }
      if (!allowedTypes.includes(file.type)) {
        alert(`File ${file.name} is not a supported format.`);
        return false;
      }
      return true;
    });

    setFormData(prev => ({
      ...prev,
      attachments: [...prev.attachments, ...validFiles]
    }));
  };

  const removeAttachment = (index) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const getSubmitButtonText = () => {
    const baseText = isSubmitting ? 'Submitting...' : 'Submit';

    if (isSubmitting) return baseText;

    switch(formData.type) {
      case 'demo': return 'Submit Demo Request';
      case 'feedback': return 'Submit Feedback';
      case 'question': return 'Submit Question';
      default: return 'Submit';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const formDataToSend = new FormData();

      // Add form fields
      formDataToSend.append('type', formData.type);
      formDataToSend.append('subject', formData.subject);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('priority', formData.priority);
      formDataToSend.append('category', formData.category);

      // Add user info
      formDataToSend.append('userName', userInfo.name);
      formDataToSend.append('userEmail', userInfo.email);
      formDataToSend.append('userProject', userInfo.project);
      formDataToSend.append('userCompany', userInfo.company);

      // Add attachments
      formData.attachments.forEach((file, index) => {
        formDataToSend.append(`attachment_${index}`, file);
      });

      console.log('Submitting feedback with formData:', formDataToSend);
      console.log('User Info:', userInfo);

      const response = await fetch(appConfig.getUrl('/submit_feedback'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken') || ''}`
        },
        body: formDataToSend
      });

      const result = await response.json();

      if (response.ok && result.status === 'Success') {
        setSubmitStatus('success');
        setShowThankYouDialog(true);
        // Reset form
        setFormData({
          type: 'feedback',
          subject: '',
          description: '',
          priority: 'medium',
          category: 'general',
          attachments: []
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleThankYouClose = () => {
    setShowThankYouDialog(false);
    setTimeout(() => {
      if (isLoggedIn) {
        navigate('/RequirementsPortal');
      } else {
        // For non-logged-in users, you might want to navigate to a different page
        // or stay on the same page
        window.location.reload();
      }
    }, 100);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-white rounded-lg shadow-md p-8">
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-blue-800 mb-2 text-center">Submit Feedback, Question, or Demo Request</h1>
            <p className="text-gray-600 text-center">
              We value your input! Please share your feedback, questions, or request a product demo to learn more about ReqSharp.
            </p>
          </div>

          {submitStatus === 'error' && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-red-800">
                    There was an error submitting your {formData.type === 'demo' ? 'demo request' : formData.type}. Please try again or contact support directly.
                  </p>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* User Information - Editable for non-logged-in users */}
            <div className="bg-blue-100 p-4 rounded-md">
              <h3 className="text-lg font-medium text-blue-800 mb-3">Your Information</h3>
              {isLoggedIn ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-blue-700">Name:</span>
                    <span className="text-gray-700 ml-2">{userInfo.name || 'Not available'}</span>
                  </div>
                  <div>
                    <span className="font-medium text-blue-700">Email:</span>
                    <span className="text-gray-700 ml-2">{userInfo.email || 'Not available'}</span>
                  </div>
                  <div>
                    <span className="font-medium text-blue-700">Project:</span>
                    <span className="text-gray-700 ml-2">{userInfo.project || 'Not available'}</span>
                  </div>
                  <div>
                    <span className="font-medium text-blue-700">Company:</span>
                    <span className="text-gray-700 ml-2">{userInfo.company || 'Not available'}</span>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-blue-700 mb-1">Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={userInfo.name}
                      onChange={handleUserInfoChange}
                      placeholder="Enter your full name"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-blue-700 mb-1">Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={userInfo.email}
                      onChange={handleUserInfoChange}
                      placeholder="Enter your email address"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-blue-700 mb-1">Company *</label>
                    <input
                      type="text"
                      name="company"
                      value={userInfo.company}
                      onChange={handleUserInfoChange}
                      placeholder="Enter your company name"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-blue-700 mb-1">Project (Optional)</label>
                    <input
                      type="text"
                      name="project"
                      value={userInfo.project}
                      onChange={handleUserInfoChange}
                      placeholder="Enter project name if applicable"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Type Selection */}
            <div>
              <label className="block text-sm font-medium text-blue-700 mb-2">
                Type of Submission
              </label>
              <div className="flex flex-wrap gap-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="type"
                    value="feedback"
                    checked={formData.type === 'feedback'}
                    onChange={handleInputChange}
                    className="mr-2 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-gray-700">Feedback</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="type"
                    value="question"
                    checked={formData.type === 'question'}
                    onChange={handleInputChange}
                    className="mr-2 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-gray-700">Question</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="type"
                    value="demo"
                    checked={formData.type === 'demo'}
                    onChange={handleInputChange}
                    className="mr-2 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-gray-700">Requesting Demo</span>
                </label>
              </div>
            </div>

            {/* Category */}
            <div>
              <label htmlFor="category" className="block text-sm font-medium text-blue-700 mb-2">
                Category
              </label>
              <select
                name="category"
                id="category"
                value={formData.category}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="general">General</option>
                <option value="feature_request">Feature Request</option>
                <option value="bug_report">Bug Report</option>
                <option value="user_interface">User Interface</option>
                <option value="performance">Performance</option>
                <option value="integration">Integration</option>
                <option value="reporting">Reporting</option>
                <option value="training">Training/Documentation</option>
                <option value="demo">Demo</option>
                <option value="other">Other</option>
              </select>
            </div>

            {/* Priority */}
            <div>
              <label htmlFor="priority" className="block text-sm font-medium text-blue-700 mb-2">
                Priority
              </label>
              <select
                name="priority"
                id="priority"
                value={formData.priority}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>

            {/* Subject */}
            <div>
              <label htmlFor="subject" className="block text-sm font-medium text-blue-700 mb-2">
                Subject *
              </label>
              <input
                type="text"
                name="subject"
                id="subject"
                value={formData.subject}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Brief description of your feedback, question, or demo request"
              />
            </div>

            {/* Description */}
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-blue-700 mb-2">
                Description *
              </label>
              <textarea
                name="description"
                id="description"
                value={formData.description}
                onChange={handleInputChange}
                required
                rows={6}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder={
                  formData.type === 'demo'
                    ? "Please provide details about your requirements, team size, and preferred demo timing..."
                    : "Please provide detailed information about your feedback or question..."
                }
              />
            </div>

            {/* File Attachments */}
            <div>
              <label className="block text-sm font-medium text-blue-700 mb-2">
                Attachments (Optional)
              </label>
              <div className="border-2 border-dashed border-blue-300 rounded-md p-4 bg-blue-50">
                <input
                  type="file"
                  multiple
                  onChange={handleFileUpload}
                  accept=".jpg,.jpeg,.png,.gif,.pdf,.txt,.doc,.docx"
                  className="w-full"
                />
                <p className="text-sm text-blue-600 mt-2">
                  Supported formats: JPG, PNG, GIF, PDF, TXT, DOC, DOCX (Max 10MB each)
                </p>
              </div>

              {/* Display selected files */}
              {formData.attachments.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-sm font-medium text-blue-700 mb-2">Selected Files:</h4>
                  <div className="space-y-2">
                    {formData.attachments.map((file, index) => (
                      <div key={index} className="flex items-center justify-between bg-blue-50 p-2 rounded border">
                        <span className="text-sm text-gray-700">{file.name}</span>
                        <button
                          type="button"
                          onClick={() => removeAttachment(index)}
                          className="text-red-500 hover:text-red-700 px-2 py-1 rounded"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Submit Button */}
            <div className="flex justify-center space-x-4 pt-4">
              <button
                type="button"
                onClick={() => {
                  if (isLoggedIn) {
                    navigate('/RequirementsPortal');
                  } else {
                    window.history.back();
                  }
                }}
                className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 bg-white"
              >
                Close
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {getSubmitButtonText()}
              </button>
            </div>
          </form>
        </div>
      </div>

      <ThankYouDialog
        isOpen={showThankYouDialog}
        onClose={handleThankYouClose}
        submissionType={formData.type}
      />
    </div>
  );
};

export default SubmitFeedback;